import * as Yup from "yup";
import { useState, useEffect } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────

export const ITEMS_PER_PAGE = 10;

export const COVERAGE_OPTIONS = [
  { label: "₹3,000", value: 3000 },
  { label: "₹5,000", value: 5000 },
];

export const BAG_COUNT_OPTIONS = Array.from({ length: 6 }, (_, i) => i + 1);

// ─── Validation Schema ────────────────────────────────────────────────────────
// bag_coverages is intentionally excluded here — it is validated manually
// via Formik's validate prop in IssuePolicyModal because Yup's .of() does not
// reliably populate errors at every array index when combined with submitCount.

export const issueBagProtectionPolicySchema = Yup.object().shape({
  no_of_bags: Yup.number()
    .typeError("Please enter a valid number of bags")
    .integer("Number of bags must be an integer")
    .min(1, "Number of bags must be at least 1")
    .max(6, "Number of bags must be at most 6")
    .required("Please enter number of bags"),
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Please enter email"),
  first_name: Yup.string()
    .trim()
    .max(25, "First name must be 25 characters or fewer")
    .required("Please enter first name"),
  last_name: Yup.string()
    .trim()
    .max(25, "Last name must be 25 characters or fewer")
    .required("Please enter last name"),
  phone_number: Yup.string()
    .matches(/^\d{10}$/, "Please enter a valid 10-digit mobile number")
    .required("Please enter phone number"),
});

// ─── Coverage Array Validator ─────────────────────────────────────────────────
// Called from Formik's validate prop. Returns errors object with
// bag_coverages as a sparse array of per-index error strings.
export const validateCoverages = (values) => {
  const errors = {};
  const count = Number(values.no_of_bags) || 1;
  const coverages = values.bag_coverages || [];

  const coverageErrors = [];
  let hasCoverageError = false;

  for (let i = 0; i < count; i++) {
    const v = coverages[i];
    const isEmpty =
      v === "" || v === null || v === undefined || Number.isNaN(Number(v));
    if (isEmpty) {
      coverageErrors[i] = "Please select bag coverage amount";
      hasCoverageError = true;
    } else {
      coverageErrors[i] = undefined;
    }
  }

  if (hasCoverageError) {
    errors.bag_coverages = coverageErrors;
  }

  return errors;
};

// ─── Formatters ───────────────────────────────────────────────────────────────

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `₹${Number(value).toLocaleString("en-IN")}`;
};

export const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const capitalizeFirst = (value) => {
  if (!value) return "-";
  const text = String(value).trim();
  if (!text) return "-";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getCoverageDates = () => {
  const startDate = new Date();
  const expiryDate = new Date(startDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  return {
    insurance_coverage_startdate: startDate.toISOString().split("T")[0],
    bag_insurance_expiry_date: expiryDate.toISOString().split("T")[0],
  };
};

export const canResendLink = (item) => {
  const status = String(item?.tag_status || "")
    .trim()
    .toLowerCase();
  return status === "inactive";
};

export const getPolicyModalInitialValues = (policy = null) => ({
  no_of_bags: Number(policy?.no_of_bags) || 1,
  bag_coverages: syncCoverages(
    Array.isArray(policy?.insurance_values)
      ? policy.insurance_values.map((value) => value ?? "")
      : [],
    Number(policy?.no_of_bags) || 1
  ),
  email: policy?.email || "",
  first_name: policy?.first_name || "",
  last_name: policy?.last_name || "",
  phone_number: policy?.phone_number || "",
});

export const getPolicyRowId = (item) =>
  item?._id || `${item?.policy_number || "policy"}-${item?.email || "row"}`;

export const sanitizeFileName = (value) =>
  String(value || "row")
    .replace(/[^a-z0-9-_]+/gi, "_")
    .replace(/^_+|_+$/g, "");

/** Sync bag_coverages array length to match bagCount, preserving existing values */
export const syncCoverages = (existing = [], bagCount) => {
  const result = [...existing];
  while (result.length < bagCount) result.push("");
  result.length = bagCount;
  return result;
};
export const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};
